// Setup global console.log filtering by category feature flags
// This file should be imported as early as possible (e.g. in main.tsx) so that
// all subsequent console.log calls are routed through this interceptor.

// Preserve the original console.log implementation
const originalConsoleLog = console.log.bind(console);

// Define regex patterns that map a call-site (file path in stack trace) to a
// specific feature-flag key.
interface CategoryMapEntry {
  pattern: RegExp;
  flag: string;
}

const categoryMap: CategoryMapEntry[] = [
  { pattern: /components\/comments/i, flag: 'showCommentsLogs' },
  { pattern: /components\/(Mindmap|mindmap)/i, flag: 'showMindmapLogs' },
  { pattern: /components\/flow/i, flag: 'showFlowLogs' },
  { pattern: /onboarding/i, flag: 'showProfileLogs' },
  { pattern: /api\//i, flag: 'showNetworkLogs' },
  { pattern: /db\//i, flag: 'showDatabaseLogs' },
  { pattern: /components\//i, flag: 'showUILogs' }, // generic UI logs
];

// Fallback flag when no pattern matches
const defaultFlag = 'showGeneralLogs';

/**
 * Attempt to infer a logging category from the call-site path using the stack
 * trace. Stack format is highly environment-dependent, so we keep parsing
 * minimal: look for the first "src/..." path in the trace.
 */
function inferFlagFromStack(): string {
  const err = new Error();
  if (!err.stack) return defaultFlag;

  const stackLines = err.stack.split('\n');
  for (const line of stackLines) {
    const match = line.match(/\((.*src\/[^)]+)\)/) || line.match(/at (.*src\/[^:]+):/);
    if (match && match[1]) {
      const path = match[1];
      for (const entry of categoryMap) {
        if (entry.pattern.test(path)) {
          return entry.flag;
        }
      }
      // If no map entry matches but we found a src path, return generic UI
      return 'showUILogs';
    }
  }
  return defaultFlag;
}

// Get appStore lazily to avoid circular dependencies and early initialization
function getFeatureFlagValue(flag: string): boolean {
  // Try to get appStore if it exists
  try {
    // We need to check if the app has been initialized
    const w = window as any;
    if (w.__APP_STORE__) {
      const appStore = w.__APP_STORE__;
      if (appStore?.featureFlags) {
        return (appStore.featureFlags as any)[flag] || false;
      }
    }
    // If appStore doesn't exist yet, allow all logs during initialization
    return true;
  } catch (e) {
    // If there's any error accessing the store, allow the log
    return true;
  }
}

// DISABLED: This was causing infinite loops by accessing MobX observables on every log
// Override console.log
// console.log = (...args: any[]) => {
//   const flag = inferFlagFromStack();
//   // Only emit if the corresponding feature flag is enabled
//   if (getFeatureFlagValue(flag)) {
//     originalConsoleLog(...args);
//   }
// };

// Use original console.log without filtering to prevent MobX observer loops
console.log = originalConsoleLog;

// Still allow console.error and console.warn unfiltered (or you could replicate
// similar filtering if desired)

export {}; 